import { useState, useCallback, useEffect } from 'react';

const DEFAULT_SORT_KEY = 'position';
const DEFAULT_SORT_DIR = 'asc';

// Module-level cache so sort state survives component unmounts (e.g. view mode switches).
const sortStateCache = new Map();

const useSortState = (cacheKey) => {
  // The cache is read only here, on mount. That is correct only because the
  // table remounts whenever the event/round it shows changes — its ancestors
  // are keyed by resultScopeId / selectedEventId. If that keying is ever
  // removed, a mounted table could keep another event's sort; re-sync here.
  const [sortState, setSortState] = useState(() => {
    return sortStateCache.get(cacheKey) || { key: DEFAULT_SORT_KEY, dir: DEFAULT_SORT_DIR };
  });

  // Pure updater; the cache write is a side effect, so it lives in the effect
  // below rather than inside setState (updaters may run more than once).
  const toggleSort = useCallback((key) => {
    setSortState((prev) => (prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' }));
  }, []);

  useEffect(() => {
    if (cacheKey) sortStateCache.set(cacheKey, sortState);
  }, [cacheKey, sortState]);

  return { sortKey: sortState.key, sortDir: sortState.dir, toggleSort };
};

export default useSortState;
